import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Check, ArrowRight, Loader, CheckCircle, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookAppointmentPage = () => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    service_type: '', date: '', time: '',
    is_online: true, notes: '',
    client_name: '', client_email: '', client_phone: ''
  });

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  useEffect(() => {
    axios.get(`${API}/appointments/services`).then(r => setServices(r.data.services || []));
  }, []);

  useEffect(() => {
    if (form.date) {
      setLoadingSlots(true);
      setForm(f => ({ ...f, time: '' }));
      axios.get(`${API}/appointments/slots?date=${form.date}`)
        .then(r => setSlots(r.data.slots || []))
        .finally(() => setLoadingSlots(false));
    }
  }, [form.date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/appointments`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ paddingTop: '72px' }} className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl p-12 shadow-floating text-center max-w-lg w-full border border-gray-100" data-testid="booking-success">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#10B98120' }}>
            <CheckCircle size={40} style={{ color: '#10B981' }} />
          </div>
          <h2 className="font-heading text-3xl font-bold text-[#0B1F3A] mb-3">Appointment Booked!</h2>
          <p className="text-[#475569] mb-2">
            Your appointment has been scheduled for <strong>{form.date}</strong> at <strong>{form.time}</strong>.
          </p>
          <p className="text-[#475569] text-sm mb-8">
            A confirmation has been sent to <strong>{form.client_email}</strong>. We look forward to working with you!
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/">
              <button className="btn-primary px-8 py-3">Back to Home</button>
            </Link>
            <Link to="/portal">
              <button className="px-8 py-3 rounded-full border-2 border-[#0B1F3A] text-[#0B1F3A] font-semibold hover:bg-gray-50 transition-all duration-200">
                Access Portal
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '72px' }}>
      {/* Hero */}
      <section className="py-14 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="label-uppercase mb-3" style={{ color: '#D4AF37' }}>Free Consultation</div>
          <h1 className="font-heading text-4xl font-extrabold text-white tracking-tight mb-3">
            Book Your Appointment
          </h1>
          <p className="text-blue-200">Schedule a consultation with a tax professional — in-person or online.</p>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto">
          {/* Steps indicator */}
          <div className="flex items-center justify-center mb-10">
            {[1, 2, 3].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step >= s ? 'text-[#0B1F3A]' : 'text-[#94A3B8]'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-200 ${
                    step > s ? 'bg-[#10B981] text-white' : step === s ? 'bg-[#0B1F3A] text-white' : 'bg-gray-200 text-[#94A3B8]'
                  }`}>
                    {step > s ? <Check size={16} /> : s}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">
                    {s === 1 ? 'Service & Date' : s === 2 ? 'Time Slot' : 'Your Details'}
                  </span>
                </div>
                {i < 2 && <div className={`h-px w-12 mx-3 transition-colors duration-200 ${step > s ? 'bg-[#0B1F3A]' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
            <form onSubmit={handleSubmit}>
              {/* Step 1 */}
              {step === 1 && (
                <div className="animate-fadeIn">
                  <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6">Select Service & Date</h2>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-2">Service Type *</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {services.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, service_type: s }))}
                            className={`text-left px-4 py-3 rounded-xl border text-sm transition-all duration-200 ${
                              form.service_type === s
                                ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white'
                                : 'border-gray-200 hover:border-[#0B1F3A] text-[#475569]'
                            }`}
                            data-testid={`service-option-${s.replace(/\s/g, '-').toLowerCase()}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-2">Preferred Date *</label>
                      <input
                        type="date"
                        value={form.date}
                        min={today}
                        max={maxDateStr}
                        onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                        className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10 transition-all duration-200"
                        data-testid="date-picker"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-2">Meeting Type</label>
                      <div className="flex gap-3">
                        {[{ val: true, label: 'Online (Video/Phone)' }, { val: false, label: 'In-Person (Kennesaw, GA)' }].map(({ val, label }) => (
                          <button
                            key={label}
                            type="button"
                            onClick={() => setForm(f => ({ ...f, is_online: val }))}
                            className={`flex-1 py-3 px-4 rounded-xl border text-sm font-medium transition-all duration-200 ${
                              form.is_online === val
                                ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white'
                                : 'border-gray-200 text-[#475569] hover:border-[#0B1F3A]'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-8">
                    <button
                      type="button"
                      disabled={!form.service_type || !form.date}
                      onClick={() => setStep(2)}
                      className="btn-primary px-8 py-3 disabled:opacity-50"
                      data-testid="next-step-1-btn"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="animate-fadeIn">
                  <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-2">Select a Time Slot</h2>
                  <p className="text-[#475569] text-sm mb-6">Available times for {form.date}</p>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-12 text-[#94A3B8]">
                      <Loader size={24} className="animate-spin mr-3" /> Loading available slots...
                    </div>
                  ) : slots.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-[#94A3B8] mb-4">No available slots for this date.</div>
                      <button type="button" onClick={() => setStep(1)} className="text-[#2563EB] font-semibold text-sm hover:underline">
                        Choose a different date
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {slots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, time: slot }))}
                          className={`py-3 rounded-xl border text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-1.5 ${
                            form.time === slot
                              ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white'
                              : 'border-gray-200 text-[#475569] hover:border-[#0B1F3A]'
                          }`}
                          data-testid={`time-slot-${slot}`}
                        >
                          <Clock size={13} />
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-4 justify-between mt-8">
                    <button type="button" onClick={() => setStep(1)} className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#475569] font-semibold hover:border-gray-300 transition-all duration-200">
                      ← Back
                    </button>
                    <button
                      type="button"
                      disabled={!form.time}
                      onClick={() => setStep(3)}
                      className="btn-primary px-8 py-3 disabled:opacity-50"
                      data-testid="next-step-2-btn"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3 */}
              {step === 3 && (
                <div className="animate-fadeIn">
                  <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-6">Your Contact Details</h2>
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Full Name *</label>
                        <input
                          value={form.client_name}
                          onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                          required
                          placeholder="John Smith"
                          className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                          data-testid="client-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Phone</label>
                        <input
                          value={form.client_phone}
                          onChange={(e) => setForm(f => ({ ...f, client_phone: e.target.value }))}
                          placeholder="(770) 000-0000"
                          className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        value={form.client_email}
                        onChange={(e) => setForm(f => ({ ...f, client_email: e.target.value }))}
                        required
                        placeholder="john@example.com"
                        className="w-full h-12 px-4 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] transition-all"
                        data-testid="client-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B1F3A] uppercase tracking-wide mb-1.5">Additional Notes</label>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                        rows={3}
                        placeholder="Any questions or specific topics you'd like to discuss?"
                        className="w-full px-4 py-3 bg-[#F8FAFC] border border-gray-200 rounded-xl text-sm outline-none focus:border-[#0B1F3A] resize-none transition-all"
                      />
                    </div>
                    {/* Booking Summary */}
                    <div className="p-5 rounded-xl" style={{ background: '#0B1F3A10', border: '1px solid #0B1F3A20' }}>
                      <div className="text-xs font-semibold text-[#0B1F3A] uppercase tracking-widest mb-3">Booking Summary</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-[#475569]">Service:</div><div className="font-medium text-[#0B1F3A]">{form.service_type}</div>
                        <div className="text-[#475569]">Date:</div><div className="font-medium text-[#0B1F3A]">{form.date}</div>
                        <div className="text-[#475569]">Time:</div><div className="font-medium text-[#0B1F3A]">{form.time}</div>
                        <div className="text-[#475569]">Type:</div><div className="font-medium text-[#0B1F3A]">{form.is_online ? 'Online' : 'In-Person'}</div>
                      </div>
                    </div>
                    {error && <div className="text-red-500 text-sm bg-red-50 p-3 rounded-xl">{error}</div>}
                  </div>
                  <div className="flex gap-4 justify-between mt-8">
                    <button type="button" onClick={() => setStep(2)} className="px-6 py-3 rounded-full border-2 border-gray-200 text-[#475569] font-semibold hover:border-gray-300 transition-all duration-200">
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !form.client_name || !form.client_email}
                      className="btn-primary px-8 py-3 disabled:opacity-50"
                      data-testid="confirm-booking-btn"
                    >
                      {submitting ? <><Loader size={16} className="animate-spin" /> Booking...</> : <>Confirm Appointment <Check size={16} /></>}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Help */}
          <div className="mt-8 text-center text-sm text-[#475569]">
            Prefer to call? <a href="tel:7702356999" className="font-semibold text-[#0B1F3A] hover:underline flex items-center gap-1 inline-flex"><Phone size={14} /> 770-235-6999</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookAppointmentPage;
